import React from "react";
import { Link } from "react-router-dom";
import { TimerIcon, TagIcon, SealCheckIcon, TruckIcon } from "@phosphor-icons/react";
import TrustTierBadge from "@/components/TrustTierBadge";
import VerifiedBadge from "@/components/VerifiedBadge";
import { SITE_ORIGIN } from "@/lib/api";

const fileUrl = (path) =>
  path ? `${SITE_ORIGIN}/api/files/${path}` : null;

// A listing is "free shipping" when the seller explicitly flagged it, or
// when the legacy flat shipping_estimate is exactly $0. We check both so
// the badge keeps working for listings created before the new shipping
// system shipped.
const isFreeShipping = (l) => {
  if (l?.shipping?.free_shipping === true) return true;
  const est = l?.shipping_estimate;
  return est !== null && est !== undefined && Number(est) === 0;
};

export default function ProductCard({ listing }) {
  const image = listing.images?.[0] ? fileUrl(listing.images[0]) : null;
  const priceLabel =
    listing.type === "auction"
      ? `$${(listing.current_bid || listing.starting_bid || listing.price).toFixed(2)}`
      : `$${Number(listing.price).toFixed(2)}`;
  const freeShip = isFreeShipping(listing);
  return (
    <Link
      to={`/listing/${listing.id}`}
      className="group block card-modern hover-lift"
      data-testid={`product-card-${listing.id}`}
    >
      <div className="aspect-square bg-zinc-100 relative overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={listing.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="h-full grid place-items-center text-zinc-400 font-mono text-xs">
            {listing.category}
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
          {listing.is_sponsored && (
            <span
              className="pill bg-amber-400 text-amber-950 font-black"
              data-testid={`sponsored-pill-${listing.id}`}
            >
              SPONSORED
            </span>
          )}
          <span
            className={`pill ${
              listing.segment === "wholesale"
                ? "bg-amber-500 text-white"
                : "bg-[#1D78AB] text-white"
            }`}
            data-testid={`segment-badge-${listing.id}`}
          >
            {listing.segment}
          </span>
          <span className="pill pill-dark">
            {listing.type === "auction" ? (
              <>
                <TimerIcon size={10} /> AUCTION
              </>
            ) : (
              <>
                <TagIcon size={10} /> BUY NOW
              </>
            )}
          </span>
        </div>
        {freeShip && (
          <span
            className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white text-[10px] font-black tracking-widest uppercase px-2.5 py-1 shadow-lg shadow-emerald-500/25"
            data-testid={`free-shipping-badge-${listing.id}`}
          >
            <TruckIcon size={11} weight="fill" />
            Free ship
          </span>
        )}
      </div>
      <div className="p-4 space-y-2">
        <h3 className="font-display font-semibold text-base leading-tight line-clamp-2 min-h-[2.5rem]">
          {listing.title}
        </h3>
        <div className="flex items-center justify-between">
          <span className="font-mono font-black text-lg text-[#1D78AB] tracking-tight">
            {priceLabel}
          </span>
          {listing.type === "auction" && (
            <span className="text-[11px] text-zinc-500 font-mono">
              {listing.bid_count} bid{listing.bid_count === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-zinc-500 pt-1 border-t border-zinc-100">
          <SealCheckIcon size={14} className="text-[#1D78AB] flex-shrink-0" />
          <span className="truncate flex-1 inline-flex items-center gap-1">
            {listing.seller_name}
            <VerifiedBadge visible={listing.seller_verified_badge} size={12} />
          </span>
          {listing.seller_trust_tier && (
            <TrustTierBadge tier={listing.seller_trust_tier} size="xs" withIcon={false} />
          )}
        </div>
      </div>
    </Link>
  );
}
